<script setup>
/**
 * The conversations this ticket is about.
 *
 * Read-only on purpose: the conversation stays the two-way surface, so this
 * panel names each link, says what role it plays, and hands the agent over to
 * the real thing. It shows no messages — a slice of a thread rendered here would
 * be a second, worse inbox.
 *
 * At most one link is the primary customer conversation: it is the one a
 * progress update is published into, so it is marked rather than left to be
 * inferred from position.
 *
 * A row can arrive `restricted`: the viewer can see the ticket but not the
 * inbox the conversation lives in, so the server sent the number and nothing
 * else. The row still renders — the work is real — but it names no customer and
 * offers no jump, because the inbox would refuse it anyway.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  // `{ id, role, conversationId, conversation: {...} }`, camelised.
  links: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['open', 'unlink']);

const { t } = useI18n();

const ROLE_LABEL = {
  primary_customer: 'SUPPORT_CASES.DETAIL.LINKS.ROLE.PRIMARY_CUSTOMER',
  source: 'SUPPORT_CASES.DETAIL.LINKS.ROLE.SOURCE',
  related: 'SUPPORT_CASES.DETAIL.LINKS.ROLE.RELATED',
};

const rows = computed(() =>
  props.links.map(link => ({
    link,
    restricted: Boolean(link.conversation?.restricted),
    displayId: link.conversation?.displayId,
    contact: link.conversation?.contact || {},
    inbox: link.conversation?.inbox || {},
    roleLabel: t(ROLE_LABEL[link.role] || ROLE_LABEL.related),
  }))
);
</script>

<template>
  <section class="flex flex-col gap-3" data-testid="case-links-panel">
    <div class="flex items-baseline gap-2">
      <h3 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('SUPPORT_CASES.DETAIL.LINKS.TITLE') }}
      </h3>
      <span
        v-if="rows.length"
        class="text-xs tabular-nums text-n-slate-11"
        data-testid="case-links-count"
      >
        {{ rows.length }}
      </span>
    </div>

    <p
      v-if="!rows.length"
      class="text-sm text-n-slate-11"
      data-testid="case-links-empty"
    >
      {{ t('SUPPORT_CASES.DETAIL.LINKS.EMPTY') }}
    </p>

    <ul v-else class="flex flex-col gap-2">
      <li
        v-for="row in rows"
        :key="row.link.id"
        class="flex items-center gap-2 p-2 rounded-lg bg-n-alpha-1"
        :data-testid="`case-link-${row.link.id}`"
      >
        <div
          v-if="row.restricted"
          class="flex items-center justify-center rounded-full size-6 bg-n-alpha-2 text-n-slate-11"
        >
          <span class="i-lucide-lock size-3" />
        </div>
        <Avatar
          v-else
          :name="row.contact.name || ''"
          :src="row.contact.thumbnail"
          :size="24"
        />
        <div class="flex flex-col min-w-0">
          <span class="text-sm truncate text-n-slate-12">
            {{ row.restricted ? `#${row.displayId}` : row.contact.name }}
          </span>
          <span class="text-xs truncate text-n-slate-11">
            {{ row.roleLabel }} ·
            {{
              row.restricted
                ? t('SUPPORT_CASES.DETAIL.LINKS.RESTRICTED')
                : row.inbox.name
            }}
          </span>
        </div>
        <div class="flex items-center gap-1 ltr:ml-auto rtl:mr-auto">
          <Button
            v-if="!row.restricted"
            variant="ghost"
            color="slate"
            size="xs"
            icon="i-lucide-external-link"
            :aria-label="t('SUPPORT_CASES.DETAIL.LINKS.OPEN')"
            :data-testid="`case-link-open-${row.link.id}`"
            @click="emit('open', row.link)"
          />
          <Button
            variant="ghost"
            color="slate"
            size="xs"
            icon="i-lucide-unlink"
            :disabled="disabled"
            :aria-label="t('SUPPORT_CASES.DETAIL.LINKS.UNLINK')"
            :data-testid="`case-link-unlink-${row.link.id}`"
            @click="emit('unlink', row.link)"
          />
        </div>
      </li>
    </ul>
  </section>
</template>
